/**
 * Feedback (docs/UI.md "Report a problem", docs/ONLINE.md "Problem reports", docs/ONLINE-CLIENT.md "After a deploy"):
 *
 *  - "Report a problem" (pause menu, F8 on a keyboard, `__bh.reportProblem`): a short form; the note goes to the
 *    server with the context below, offline it waits on the device (Reporter) and "Save as file" exports it;
 *  - automatic telemetry (AutoReporter): uncaught errors, unhandled rejections, WebGL context loss, a sustained low
 *    frame rate — de-duplicated, rate limited, no log lines or names;
 *  - "A new version of Borough Heat is out": when the server speaks a newer protocol (kick `protocol`, or
 *    /api/shards at boot) a card offers the reload; a build change with the same protocol is a quiet toast.
 *
 * Context sent with a report: build + protocol, user agent, screen, touch, graphics tier, frame rate, position,
 * game / player mode, connection state, shard, online entity id, page uptime; a player's own report also carries the
 * last 50 client log lines (the form says so). Never money, never chat, never other players.
 */
import { PROTOCOL_VERSION } from '../../shared/protocol';
import type { ReportCategory, ReportContext, ReportPayload } from '../../shared/feedback';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { CLIENT_BUILD } from './build';
import { createClientLog, installClientLog, type ClientLog } from './clientLog';
import { ReportForm } from './reportForm';
import { Reporter, type SendOutcome } from './reporter';
import { AutoReporter } from './telemetry';
import { isServerNewer, protocolFromKick, reloadToNewBuild, UpdateCard } from './updateCard';

export interface FeedbackService {
  /** Open the "Report a problem" form (no-op headless or when it is already up). */
  open(): void;
  readonly isOpen: boolean;
  /** Send a report without the form (tests, other modules). */
  report(message: string, category?: ReportCategory | null): Promise<SendOutcome>;
  /** Origin prefix for the API ('' = same origin; headless tests point it at their server). */
  setBase(base: string): void;
}

declare module '../core/services' {
  interface ServiceMap {
    feedback: FeedbackService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Send a problem report (as the form's SEND does). Resolves to 'sent' | 'queued' | 'dropped'. */
    reportProblem(message: string, category?: string): Promise<string>;
    /** Open the "Report a problem" form. */
    openReportForm(): void;
    /** Queue, last sends, automatic reports this session, whether the form / update card is up. */
    feedbackState(): { queued: number; history: { kind: string; outcome: string; status: number }[]; auto: number; formOpen: boolean; updateCard: string | null; build: string };
    /** Report an error as the page's error handler would (tests of the telemetry path). */
    feedbackTestError(message: string): void;
  }
}

const FPS_SAMPLE_S = 2;

export function createFeedbackSystem(): System {
  let ctx: GameContext;
  let log: ClientLog;
  let reporter: Reporter;
  let auto: AutoReporter;
  let base = '';
  let form: ReportForm | null = null;
  let card: UpdateCard | null = null;
  let cardReason: string | null = null;
  let inputWas = true;
  let pausedByUs = false;
  let fpsAcc = 0;
  let tickAcc = 0;
  let buildToastShown = false;
  const offs: (() => void)[] = [];
  const browser = typeof window !== 'undefined' && typeof document !== 'undefined';

  const net = () => ctx.services.tryGet('net');

  function context(withLog: boolean): ReportContext {
    const c: ReportContext = { build: CLIENT_BUILD, protocol: PROTOCOL_VERSION };
    if (browser) {
      c.ua = navigator.userAgent.slice(0, 200);
      c.screen = { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 };
      c.touch = (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window;
      c.uptimeS = Math.round(performance.now() / 1000);
    }
    const tier = ctx.services.tryGet('renderPipeline')?.quality;
    if (tier) c.quality = String(tier);
    try {
      const perf = ctx.hooks.call('getPerf') as { fps?: number; frameMsMax?: number; frameMs?: number };
      if (typeof perf.fps === 'number' && perf.fps > 0) c.fps = { avg: perf.fps, low: perf.frameMsMax ? 1000 / perf.frameMsMax : perf.fps, n: 120 };
    } catch {
      /* no perf hook (headless test games still have it) */
    }
    const p = ctx.services.tryGet('player');
    if (p) {
      c.pos = { x: Math.round(p.pos.x * 10) / 10, z: Math.round(p.pos.z * 10) / 10 };
      c.mode = `${ctx.state.mode}/${p.mode}`;
    } else c.mode = ctx.state.mode;
    const n = net();
    if (n) {
      c.net = n.status;
      if (n.shard) c.shard = n.shard.id;
      if (n.selfEid) c.eid = n.selfEid;
    } else c.net = 'offline';
    if (withLog) c.log = log.lines();
    return c;
  }

  function payload(message: string, category: ReportCategory | null): ReportPayload {
    const p: ReportPayload = { kind: 'report', message, at: Date.now(), context: context(true) };
    if (category) p.category = category;
    return p;
  }

  function attachments(): string {
    const online = !!net()?.online;
    return `your device and screen, graphics quality, frame rate, where you are and what you were doing${online ? ', your online name' : ''}, and the game’s last 50 log lines. Nothing else.`;
  }

  function closeForm(): void {
    if (!form) return;
    form.dispose();
    form = null;
    ctx.input.enabled = inputWas;
    if (pausedByUs) {
      pausedByUs = false;
      ctx.hooks.call('pause', false);
    }
  }

  function openForm(): void {
    if (!browser || form) return;
    const parent = ctx.uiRoot ?? document.body;
    inputWas = ctx.input.enabled;
    ctx.input.enabled = false;
    // Offline, the city waits while you write (online it can't: the server keeps going).
    if (!net()?.online && ctx.state.mode === 'playing') {
      ctx.hooks.call('pause', true);
      pausedByUs = (ctx.state.mode as string) === 'paused';
    }
    form = new ReportForm(parent, {
      attachments: attachments(),
      online: !!net()?.online,
      onSend: (r) => reporter.send(payload(r.message, r.category)),
      onSave: (r) => download(reporter.exportJson(r.message ? payload(r.message, r.category) : undefined)),
      onClose: closeForm,
    });
  }

  function download(json: string): void {
    if (!browser) return;
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `borough-heat-report-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }

  function showUpdateCard(reason: 'protocol'): void {
    if (cardReason) return;
    cardReason = reason; // the decision is observable headless too (feedbackState); the card itself needs a DOM
    if (!browser) return;
    closeForm();
    card = new UpdateCard(ctx.uiRoot ?? document.body, reason, () => void reloadToNewBuild());
  }

  /**
   * Our hello was refused with `kick protocol`. That is the "new version is out" case only when the server named a
   * newer protocol; the same kick covers a server-side error while joining, where a reload would loop. When the
   * text does not say, /api/shards decides.
   */
  function onProtocolKick(text: string | undefined): void {
    const named = protocolFromKick(text);
    if (named !== null) {
      if (isServerNewer(named, PROTOCOL_VERSION)) showUpdateCard('protocol');
      return;
    }
    void checkServer();
  }

  /** /api/shards says which protocol and build the server runs. */
  async function checkServer(): Promise<void> {
    if (!browser) return;
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 4000);
      const res = await fetch(`${base}/api/shards`, { cache: 'no-store', signal: ctl.signal });
      clearTimeout(timer);
      if (!res.ok) return;
      const j = (await res.json()) as { protocol?: unknown; build?: unknown };
      if (isServerNewer(j.protocol, PROTOCOL_VERSION)) showUpdateCard('protocol');
      else if (typeof j.build === 'string' && j.build !== 'dev' && CLIENT_BUILD !== 'dev' && j.build !== CLIENT_BUILD && !buildToastShown && net()?.online) {
        buildToastShown = true;
        ctx.services.tryGet('ui')?.toast('A new version of Borough Heat is out — reload when it suits you.', 'info', 8);
      }
    } catch {
      /* no server (offline play) */
    }
  }

  const service: FeedbackService = {
    open: openForm,
    get isOpen() {
      return !!form;
    },
    report: (message, category = null) => reporter.send(payload(message, category)),
    setBase(b) {
      base = b.replace(/\/+$/, '');
    },
  };

  return {
    id: 'feedback',
    order: 770,
    init(c) {
      ctx = c;
      log = browser ? installClientLog() : createClientLog();
      reporter = new Reporter({ base: () => base, token: () => net()?.session?.token ?? null, ...(browser ? {} : { storage: null }) });
      auto = new AutoReporter((p) => void reporter.send(p), () => context(false), () => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
      c.services.provide('feedback', service);

      c.hooks.register('reportProblem', (message, category) => service.report(String(message), (category as ReportCategory | undefined) ?? null));
      c.hooks.register('openReportForm', () => openForm());
      c.hooks.register('feedbackState', () => ({
        queued: reporter.queue.length,
        history: reporter.history.map((h) => ({ kind: h.kind, outcome: h.outcome, status: h.status })),
        auto: auto.count,
        formOpen: !!form,
        updateCard: cardReason,
        build: CLIENT_BUILD,
      }));
      c.hooks.register('feedbackTestError', (message) => auto.error('error', String(message), 'Error\n    at feedbackTestError (test)'));

      offs.push(
        c.events.on('netStatus', (e) => {
          if (e.status === 'online') {
            void reporter.flush();
            void checkServer();
          } else if (e.status === 'offline' && e.kick === 'protocol') onProtocolKick(e.reason);
        }),
      );

      if (browser) {
        const onError = (e: ErrorEvent): void => auto.error('error', e.message, e.error instanceof Error ? e.error.stack : `at ${e.filename ?? '?'}:${e.lineno ?? 0}:${e.colno ?? 0}`);
        const onRejection = (e: PromiseRejectionEvent): void => {
          const r = e.reason as unknown;
          auto.error('rejection', r instanceof Error ? `${r.name}: ${r.message}` : String(r), r instanceof Error ? r.stack : undefined);
        };
        const onKey = (e: KeyboardEvent): void => {
          if (form && e.code === 'Escape') {
            // Focus fell out of the form (a click on the backdrop): Escape still closes it.
            e.preventDefault();
            closeForm();
            return;
          }
          if (e.code !== 'F8' || e.repeat) return;
          e.preventDefault();
          if (form) closeForm();
          else openForm();
        };
        const onOnline = (): void => void reporter.flush();
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onRejection);
        window.addEventListener('keydown', onKey);
        window.addEventListener('online', onOnline);
        const canvas = c.render?.canvas;
        const onLost = (): void => auto.contextLost();
        canvas?.addEventListener('webglcontextlost', onLost);
        const flushTimer = window.setInterval(() => void reporter.flush(), 60_000);
        offs.push(() => {
          window.removeEventListener('error', onError);
          window.removeEventListener('unhandledrejection', onRejection);
          window.removeEventListener('keydown', onKey);
          window.removeEventListener('online', onOnline);
          canvas?.removeEventListener('webglcontextlost', onLost);
          window.clearInterval(flushTimer);
        });
      }
    },
    start() {
      // Whatever waited on this device from an earlier session goes now if the server is there; and a page that
      // is older than the server says so before the player even signs in.
      if (browser && reporter.queue.length) void reporter.flush();
      void checkServer();
    },
    update(dt) {
      tickAcc += dt;
      if (tickAcc >= 1) {
        tickAcc = 0;
        auto.tick();
      }
      fpsAcc += dt;
      if (fpsAcc >= FPS_SAMPLE_S) {
        const span = fpsAcc;
        fpsAcc = 0;
        const visible = !browser || document.visibilityState === 'visible';
        let fps = 0;
        try {
          fps = (ctx.hooks.call('getPerf') as { fps?: number }).fps ?? 0;
        } catch {
          fps = 0;
        }
        auto.fps(fps, span, browser && visible && ctx.state.mode === 'playing' && !form);
      }
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      closeForm();
      card?.dispose();
      card = null;
    },
  };
}
